import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useEffect } from 'react';
import { useRouter } from 'next/router';
import { CloseIcon } from '@c/icons';

interface ModalProps {
  heading?: string;
  html: string;
  isOpen: boolean;
  includeHeading?: boolean;
  bgImage?: string;
  closeIconColor?: string;
  dismiss: () => void;
  width?: string;
}

const EmbeddedModal: React.FC<ModalProps> = ({
  heading,
  html,
  isOpen,
  dismiss,
  includeHeading = true,
  bgImage,
  closeIconColor = "text-black",
  width = '80%',
}) => {
  const router = useRouter();

  useEffect(() => {
    router.beforePopState(({ as }) => {
      if (as !== router.asPath) {
        dismiss?.();
      }
      return true;
    });
    return () => {
      router.beforePopState(() => true);
    };
  }, [router, dismiss]);

  const dynamicStyle = {
    maxWidth: width,
    width: 'fit-content',
    minWidth: width ?? '32rem',
    padding: '2.4rem',
    borderRadius: '10px',
    backgroundImage: bgImage ? `url(${bgImage})` : undefined,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        // z-modal (400) — above the sticky header (200) and drawers (300).
        className="relative z-modal"
        onClose={() => dismiss?.()}
        static={false}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex h-full items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className="text-[1.5rem] flex flex-col relative bg-brand-white text-left text-brand-black shadow-xl transition-all max-h-screen overflow-auto"
                style={dynamicStyle}
              >
                <div className={`flex items-center ${includeHeading ? 'justify-between' : 'justify-end'}`}>
                  {includeHeading && (
                    <Dialog.Title>
                      <h3 className="text-[2rem] font-semibold">{heading}</h3>
                    </Dialog.Title>
                  )}
                  <button type="button" onClick={dismiss} className={closeIconColor}>
                    <CloseIcon />
                  </button>
                </div>
                <div className="pt-4" dangerouslySetInnerHTML={{ __html: html }} />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default EmbeddedModal;